import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from '@material-table/core';
import { ExportCsv } from '@material-table/exporters';
import { forwardRef } from 'react';
import { AddBox, Check, Clear, DeleteOutline, Edit, FilterList, SaveAlt, ChevronLeft, ChevronRight, FirstPage, LastPage, CloudUpload } from '@mui/icons-material';
import styled from '@emotion/styled';

// Optional: Forward icons for MaterialTable's internal use
const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  CloudUpload: forwardRef((props, ref) => <CloudUpload {...props} ref={ref} />)
};


const StyledTableContainer = styled('div')`
  .MuiInputBase-root {
    height: 25px;
    padding: 0 8px;
  }
`;

const GenericDataTable = ({ data, columns = [], onDataUpdate, onFileImport }) => {
  // Use the parent's data as the initial state
  const [tableData, setTableData] = useState(data);
  // Create a ref for the hidden file input.
  const fileInputRef = useRef(null);

  // When the file input changes, pass the file to onFileImport.
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && onFileImport) {
      onFileImport(file);
    }
    event.target.value = '';
  };

  // Update internal state if parent data changes
  useEffect(() => {
    setTableData(data);
  }, [data]);

  // Helper function to notify parent of changes (if callback provided)
  const updateData = (updatedData) => {
    setTableData(updatedData);
    if (onDataUpdate) {
      onDataUpdate(updatedData);
    }
  };

    // Custom exporter for PLT format
  const exportPlt = (columns, data, fileName, separator = '\t') => {
    // Define the first fixed row
    const firstLine = "[PLANT]";
    // Create a header row using the column titles
    const header = columns.map(col => col.title).join(separator);
    // Create data rows by mapping each row and extracting the field values
    const rows = data.map(row =>
      columns.map(col => row[col.field]).join(separator)
    );
    // Combine header and rows, separating each row by a newline
    const content = [firstLine, header, ...rows].join('\n');

    // Create a Blob with text/plain type and trigger a download
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName + '.plt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  
  return (
    <StyledTableContainer>
        <input
          type="file"
          accept=".plt"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <MaterialTable
          icons={tableIcons}
          title=""
          columns={columns}
          data={tableData}
          options={{
            selection: true,          // Enable row selection
            filtering: true,          // Enable column filtering
            actionsColumnIndex: -1,   // Display the actions column at the end
            searchFieldAlignment: 'left',
            exportAllData: true,
            exportMenu: [
              {
                label: "Export CSV",
                exportFunc: (cols, datas) =>
                  ExportCsv(cols, datas, "Meters List", ";")
              },
              {
                label: "Export PLT",
                exportFunc: (cols, datas) =>
                  exportPlt(cols, datas, "Meters Plant")
              }
            ]
          }}
          editable={{
            onRowAdd: newData =>
              new Promise((resolve) => {
                setTimeout(() => {
                  updateData([...tableData, newData]);
                  resolve();
                }, 600);
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve) => {
                setTimeout(() => {
                  const updatedData = tableData.map((row) =>
                    row.id === oldData.id ? newData : row
                  );
                  console.log(updatedData);
                  updateData(updatedData);
                  resolve();
                }, 600);
              }),
            onRowDelete: oldData =>
              new Promise((resolve) => {
                setTimeout(() => {
                  const updatedData = tableData.filter(row => row.id !== oldData.id);
                  updateData(updatedData);
                  resolve();
                }, 600);
              }),
          }}
          actions={[
            {
              tooltip: 'Delete Selected Rows',
              icon: DeleteOutline,
              onClick: (event, rows) => {
                // Use the unique id to filter out selected rows
                if (window.confirm("Are you sure you want to delete all selected rows?")) {
                  const updatedData = tableData.filter(
                    row => !rows.some(selected => selected.id === row.id)
                  );
                  updateData(updatedData);
                }
              }
            },
            {
              tooltip: 'Import PLT',
              icon: CloudUpload,
              isFreeAction: true,
              onClick: () => fileInputRef.current && fileInputRef.current.click()
            }
  
          ]}
        />
    </StyledTableContainer>
  );
};

export default GenericDataTable;